import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyGroup } from '../../services/groupService';
import { getRecipes } from '../../services/recipeService';
import { getDashboardContent } from '../../services/dashboardContentService';
import { getAddons, getPlans } from '../../services/planService';
import GroupSetup from '../groups/GroupSetup';
import GroupMembersList from '../groups/GroupMembersList';
import GroupInviteSection from '../groups/GroupInviteSection';
import PricingCards from '../pricing/PricingCards';
import ManageAddons from '../pricing/ManageAddons';
import DashboardBanner from '../dashboard/DashboardBanner';
import DashboardAnnouncement from '../dashboard/DashboardAnnouncement';
import AppLayout from '../layout/AppLayout';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [groupData, setGroupData] = useState(null);
  const [groupLoading, setGroupLoading] = useState(true);
  const [recipeCount, setRecipeCount] = useState(0);

  const [dashboardContent, setDashboardContent] = useState({ bannerImageUrl: null, announcementText: '' });

  const [addons, setAddons] = useState([]);
  const [communityMaxMembers, setCommunityMaxMembers] = useState(null);

  const fetchGroup = async () => {
    setGroupLoading(true);
    try {
      const data = await getMyGroup();
      setGroupData(data);
    } catch (err) {
      // 403 just means the user doesn't belong to a group yet -- that's expected
      setGroupData(null);
    } finally {
      setGroupLoading(false);
    }
  };

  const fetchRecipeCount = async () => {
    try {
      const data = await getRecipes({ limit: 1 });
      setRecipeCount(data.total);
    } catch (err) {
      setRecipeCount(0);
    }
  };

  const fetchDashboardContent = async () => {
    try {
      const data = await getDashboardContent();
      setDashboardContent({ bannerImageUrl: data.bannerImageUrl, announcementText: data.announcementText });
    } catch (err) {
      // non-critical -- banner/announcement just won't show
    }
  };

  useEffect(() => {
    fetchGroup();
    fetchDashboardContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (groupData) fetchRecipeCount();
  }, [groupData]);

  const subscription = groupData?.subscription;
  const isActive =
    subscription?.status === 'active' ||
    (subscription?.status === 'trial' && subscription?.trial_ends_at && new Date(subscription.trial_ends_at) > new Date());

  const isOnTrial = isActive && subscription?.status === 'trial';
  const trialDaysLeft = isOnTrial
    ? Math.max(0, Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;
  const isExpiredTrial = !isActive && subscription?.status === 'trial';

  useEffect(() => {
    if (!isActive) return;
    const loadAddons = async () => {
      try {
        const [addonsData, plansData] = await Promise.all([getAddons(), getPlans()]);
        setAddons(addonsData.addons);
        const community = plansData.plans?.find((p) => p.plan_key === 'community');
        if (community) setCommunityMaxMembers(community.max_members);
      } catch (err) {
        // non-critical -- ManageAddons just won't show purchase buttons
      }
    };
    loadAddons();
  }, [isActive]);

  const handleGroupCreated = () => {
    fetchGroup();
  };

  const isOwner = groupData?.group?.owner_id === user?.id;

  return (
    <AppLayout>
      <DashboardBanner bannerImageUrl={dashboardContent.bannerImageUrl} onUpdate={fetchDashboardContent} />
      <DashboardAnnouncement announcementText={dashboardContent.announcementText} onUpdate={fetchDashboardContent} />

      {isOnTrial && (
        <div style={styles.trialBanner}>
          🎁 You're on a free trial — <strong>{trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left</strong>.
          {isOwner && ' Purchase a plan anytime to keep going after it ends.'}
        </div>
      )}
      {isExpiredTrial && (
        <div style={styles.trialExpiredBanner}>
          Your free trial has ended. You can still view your recipes, but adding, editing,
          inviting members, meal planning, and PDF export are paused until you purchase a plan.
        </div>
      )}

      {groupLoading ? (
        <p style={styles.loadingText}>Loading your cookbook...</p>
      ) : groupData ? (
        <>
          <GroupMembersList
            group={groupData.group}
            members={groupData.members}
            subscription={groupData.subscription}
            onUpdate={fetchGroup}
          />

          {isActive && (
            <div style={styles.actionCard}>
              <h3 style={styles.actionTitle}>🚀 Get Cooking</h3>
              <p style={styles.actionText}>
                Add your favorite recipes and share them with your cookbook members.
              </p>
              <button style={styles.addButton} onClick={() => navigate('/recipes/new')}>
                + Add Your First Recipe
              </button>
            </div>
          )}

          {isActive ? (
            <GroupInviteSection
              members={groupData.members}
              subscription={groupData.subscription}
              pendingInvites={groupData.pendingInvites}
              onUpdate={fetchGroup}
            />
          ) : (
            isOwner && <PricingCards onRedeemed={fetchGroup} />
          )}

          {/* During a trial the owner still needs a way to convert: buy a plan or redeem a gift code */}
          {isOnTrial && isOwner && <PricingCards onRedeemed={fetchGroup} />}

          {/* Upgrades and scan packs only make sense on top of a real plan, not a trial */}
          {subscription?.status === 'active' && (
            <ManageAddons
              subscription={groupData.subscription}
              addons={addons}
              isOwner={isOwner}
              onUpdate={fetchGroup}
              communityMaxMembers={communityMaxMembers}
            />
          )}

          <div style={styles.statsRow}>
            <div style={styles.statCard} onClick={() => navigate('/recipes')}>
              <span style={styles.statIcon}>📖</span>
              <h3 style={styles.statNumber}>{recipeCount}</h3>
              <p style={styles.statLabel}>Recipes</p>
            </div>
          </div>
        </>
      ) : (
        <GroupSetup onGroupCreated={handleGroupCreated} />
      )}
    </AppLayout>
  );
};

const styles = {
  loadingText: {
    textAlign: 'center',
    color: '#999',
  },
  trialBanner: {
    backgroundColor: '#f5f0fa', border: '1px solid #ddc9ee', color: '#5b4a75',
    padding: '12px 18px', borderRadius: '10px', marginBottom: '20px', fontSize: '14px',
  },
  trialExpiredBanner: {
    backgroundColor: '#fdf0e3', border: '1px solid #f0c896', color: '#a05a1a',
    padding: '14px 18px', borderRadius: '10px', marginBottom: '20px', fontSize: '13px', lineHeight: 1.5,
  },
  statsRow: {
    display: 'flex',
    gap: '20px',
    marginBottom: '24px',
  },
  statCard: {
    flex: 1,
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center',
    cursor: 'pointer',
    maxWidth: '200px',
  },
  statIcon: {
    fontSize: '32px',
  },
  statNumber: {
    fontSize: '36px',
    color: '#e67e22',
    margin: '8px 0',
  },
  statLabel: {
    color: '#666',
    margin: 0,
  },
  actionCard: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center',
    marginBottom: '24px',
  },
  actionTitle: {
    color: '#333',
    fontSize: '22px',
    marginBottom: '12px',
  },
  actionText: {
    color: '#666',
    marginBottom: '20px',
  },
  addButton: {
    padding: '12px 28px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default Dashboard;
